import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ColorModeProvider } from './contexts/ColorModeContext';
import { AdminProvider } from './contexts/AdminContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/Dashboard';
import NinjaManagement from './pages/NinjaManagement';
import TaskManagement from './pages/TaskManagement';
import CustomerManagement from './pages/CustomerManagement';
import ServicesPricing from './pages/ServicesPricing';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import CouponManagement from './pages/CouponManagement';
import NinjaPreferenceApprovals from './pages/NinjaPreferenceApprovals';
import AdminProfile from './pages/AdminProfile';
import BannersAds from './pages/BannersAds';
import AreaManagement from './pages/AreaManagement';
import AreaStatistics from './pages/AreaStatistics';
import Login from './pages/Login';
import Cities from './pages/superadmin/Cities';
import AdminUsers from './pages/superadmin/AdminUsers';
import AuditLog from './pages/superadmin/AuditLog';
import RequireRole from './components/RequireRole';
import CityCatalog from './pages/CityCatalog';
import Agencies from './pages/Agencies';
import Withdrawals from './pages/Withdrawals';
import Subscriptions from './pages/Subscriptions';
import { useAdmin } from './contexts/AdminContext';

/**
 * /services is one route, two very different screens: a super_admin gets the
 * full master-catalog CRUD (ServicesPricing), a city_admin gets the narrower
 * toggle-and-price-override view (CityCatalog) — see CityCatalog's own
 * comment for why these are separate components rather than one page hiding
 * pieces of itself by role.
 */
const ServicesRoute: React.FC = () => {
  const { isActingAsCityAdmin } = useAdmin();
  return isActingAsCityAdmin ? <CityCatalog /> : <ServicesPricing />;
};

function App() {
  return (
    <ColorModeProvider>
      <AdminProvider>
        <NotificationProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Layout />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="ninjas" element={<NinjaManagement />} />
                <Route path="tasks" element={<TaskManagement />} />
                <Route path="customers" element={<CustomerManagement />} />
                <Route path="agencies" element={<Agencies />} />
                <Route path="subscriptions" element={<Subscriptions />} />
                <Route path="withdrawals" element={<Withdrawals />} />
                <Route path="services" element={<ServicesRoute />} />
                <Route path="coupons" element={<CouponManagement />} />
                <Route path="areas" element={<AreaManagement />} />
                <Route path="area-statistics" element={<AreaStatistics />} />
                <Route path="ninja-preferences" element={<NinjaPreferenceApprovals />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="banner-ads" element={<BannersAds />} />
                <Route path="settings" element={<Settings />} />
                <Route path="profile" element={<AdminProfile />} />
                <Route
                  path="super-admin/cities"
                  element={
                    <RequireRole roles={['super_admin']}>
                      <Cities />
                    </RequireRole>
                  }
                />
                <Route
                  path="super-admin/admins"
                  element={
                    <RequireRole roles={['super_admin']}>
                      <AdminUsers />
                    </RequireRole>
                  }
                />
                <Route
                  path="super-admin/audit-log"
                  element={
                    <RequireRole roles={['super_admin']}>
                      <AuditLog />
                    </RequireRole>
                  }
                />
              </Route>
            </Routes>
          </Router>
        </NotificationProvider>
      </AdminProvider>
    </ColorModeProvider>
  );
}

export default App;